import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { DerivativesOpenedPositionProjection } from 'common/api/autogenerated/trading-gateway';

import { Button, InputRange, LocalizedNumberInput } from 'common/components';

import { localizeNumber } from 'common/helpers';
import { useDerivatives, useDerivativesPositions, useI18n, useNotification, usePopup } from 'common/hooks';

import { ServerError } from 'components/ServerError';

import { DerivativeDashboardContext } from 'views/derivatives/dashboard/DerivativeDashboard.provider';
import {
  convertFromQuantityIntoContracts,
  createOrderForPartialPositionClose,
} from 'views/derivatives/dashboard/components/OrderForm/helpers';
import { isBuy } from 'views/derivatives/orders/utils';

import { truncateNumber } from 'utils/truncateNumber';

import { CloseDerivativePositionPopupProps } from './CloseDerivativePositionPopup.container';
import './index.css';

type CloseDerivativePositionFormValues = {
  quantity: DerivativesOpenedPositionProjection['quantity'];
  percentage: number | null;
};

const CloseDerivativePositionPopup = React.memo((props: CloseDerivativePositionPopupProps) => {
  const { translate } = useI18n();
  const { hide } = usePopup();
  const { notify } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [bubbleValue, setBubbleValue] = useState<number | null>(null);

  const [internalQuantityValue, setInternalQuantityValue] = useState<number | null>(null);
  const [isValidationRulesEnabled, enableValidationRules] = useState<boolean>(false);

  const {
    postOrder,
    orderBook: { bestBuy, bestSell },
  } = useContext(DerivativeDashboardContext);

  const { positions } = useDerivativesPositions();

  const position = useMemo(() => positions.find((pos) => pos.id === props.id) || {}, [positions, props.id]);

  const [baseCurrency, quotedCurrency] = useMemo(
    () => (position?.tradingPairCode || '').split('-'),
    [position?.tradingPairCode],
  );

  const {
    stream: { settlementCurrency, contractType, contractValue },
    info: { digitsForBase, minQuantity, digitsForPandL },
  } = useDerivatives(position?.tradingPairCode || '');

  const methods = useForm<CloseDerivativePositionFormValues>({
    defaultValues: {
      quantity: 0,
      percentage: null,
    },
  });

  const digits = useMemo(() => digitsForBase, [digitsForBase]);

  const { control, formState, setValue, trigger } = methods;

  const { errors, isValid } = formState;

  const formQuantity = useWatch({ control, name: 'quantity' });

  const estimatedPnL = useMemo(
    // 85523 TEMP CODE (markPrice -> lastPrice)
    () => (Number(position?.lastPriceUnrealizedPnL) * Number(formQuantity)) / Number(position?.quantity),
    [formQuantity, position?.lastPriceUnrealizedPnL, position?.quantity],
  );
  const estimatedPnLFormatted = useMemo(
    () => localizeNumber(estimatedPnL, { maxDigits: digitsForPandL }),
    [digitsForPandL, estimatedPnL],
  );
  const estimatedPnLSign = useMemo(() => {
    if (estimatedPnL < 0) return '-';
    if (estimatedPnL > 0) return '+';

    return '';
  }, [estimatedPnL]);

  const validate = useMemo(
    () => ({
      required: (v: string | number | undefined) => !!Number(v) || '',
      shouldBeMoreThenMinimumStep: (v: string | number | undefined) => {
        if (!isValidationRulesEnabled) return true;

        return (
          Number(v) >= Number(minQuantity) ||
          translate.toText('mobile:derivative:form:quantity:error:min').replace('{min}', String(minQuantity))
        );
      },

      shouldBeLessThenPositionSize: (v: string | number | undefined) => {
        if (!isValidationRulesEnabled) return true;

        return (
          Number(v) <= Number(position?.quantity) ||
          translate.toText('mobile:derivative:form:quantity:error:max').replace('{max}', String(position?.quantity))
        );
      },
    }),
    [isValidationRulesEnabled, minQuantity, position?.quantity, translate],
  );

  const isUp = useMemo(() => estimatedPnL >= 0, [estimatedPnL]);

  const estimatedPnlClassNames = classNames({
    'derivative-position-edit-popup__pnl': true,
    'derivative-position-edit-popup__pnl--up': isUp,
    'derivative-position-edit-popup__pnl--down': !isUp,
  });

  const isBuyAction = useMemo(() => isBuy(position.side), [position.side]);

  const onSubmit = useCallback(
    (values: CloseDerivativePositionFormValues) => {
      setIsLoading(true);

      const contracts = convertFromQuantityIntoContracts({
        contractType,
        contractValue,
        quantity: values.quantity,
        price: isBuyAction ? bestSell({ quantity: values.quantity }) : bestBuy({ quantity: values.quantity }),
      });

      const order = createOrderForPartialPositionClose(position, contracts);

      const isPositionFullyClosed = Number(values.quantity) === Number(position.quantity);

      return postOrder(order)
        .then(() => {
          setServerError(undefined);
          hide();
          if (!isPositionFullyClosed) {
            notify({
              title: translate
                .toText(`mobile:notifications:derivative:position:${position?.side}:updated`)
                .replace('{symbol}', `${baseCurrency}/${quotedCurrency}`),
            });
          }
        })
        .catch((err) => setServerError(err?.body?.message))
        .finally(() => setIsLoading(false));
    },
    [
      baseCurrency,
      bestBuy,
      bestSell,
      contractType,
      contractValue,
      hide,
      isBuyAction,
      notify,
      position,
      postOrder,
      quotedCurrency,
      translate,
    ],
  );

  const onChangeBalanceSlider = useCallback(
    (slider: string) => {
      setBubbleValue(+slider / Number(position?.quantity));
      setInternalQuantityValue(Number(slider));

      const sliderAsNumber = Number(slider);

      const newQuantity = truncateNumber(sliderAsNumber, digits);
      if (!newQuantity) setValue('quantity', 0);
      else setValue('quantity', newQuantity);
    },
    [digits, position?.quantity, setValue],
  );

  useEffect(() => {
    if (bubbleValue) setValue('quantity', Number(position.quantity) * bubbleValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.quantity]);

  const label = useMemo(
    () => `${translate.toText('mobile:derivative:form:quantity:label')}, ${baseCurrency}`,
    [baseCurrency, translate],
  );

  useEffect(
    function validateForm() {
      trigger('quantity');
    },
    [trigger, position?.quantity, formQuantity, isValidationRulesEnabled],
  );

  const onBlur = useCallback(() => {
    enableValidationRules(true);
  }, []);

  const onQuantityInputChangeHandler = useCallback((value: string) => {
    setInternalQuantityValue(Number(value));
  }, []);

  const balanceRangeStep = useMemo(() => Number(position?.quantity) / 100, [position?.quantity]);

  return (
    <FormProvider {...methods}>
      <form className='derivative-position-edit-popup' onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='derivative-position-edit-popup__fields'>
          <LocalizedNumberInput
            name='quantity'
            data-test='derivative-order-form-quantity'
            digits={digits}
            value={formQuantity}
            showSteps
            max={position?.quantity}
            validate={validate}
            error={errors.quantity?.message}
            onBlur={onBlur}
            label={label}
            onChange={onQuantityInputChangeHandler}
            autoFocus
          />

          <p className='derivative-position-edit-popup__current'>
            <span className='derivative-position-edit-popup__current-title'>
              {translate.toText('mobile:derivative:position:popup:close-partially:current:label')}
            </span>
            <span className='derivative-position-edit-popup__current-value'>
              {position?.quantity} {baseCurrency}
            </span>
          </p>
          <p className={estimatedPnlClassNames}>
            <span className='derivative-position-edit-popup__pnl-title'>
              {translate.toText('mobile:derivative:position:popup:close-partially:estimated-pnl:label')}
            </span>
            <span className='derivative-position-edit-popup__pnl-value'>
              {!!estimatedPnL && estimatedPnLSign}
              {(!!estimatedPnL && estimatedPnLFormatted) || '--.--'} {settlementCurrency}
            </span>
          </p>
          <InputRange
            value={internalQuantityValue || 0}
            maxValue={Number(position?.quantity)}
            step={balanceRangeStep}
            onChange={onChangeBalanceSlider}
          />
        </div>

        {serverError && <ServerError error={serverError} className='derivative-position-edit-popup__server-error' />}

        <Button disabled={!isValid} loading={isLoading} type='submit' large>
          {translate.toText('mobile:derivative:position:popup:close-partially:submit-button')}
        </Button>
      </form>
    </FormProvider>
  );
});

export default CloseDerivativePositionPopup;
